import { makeAutoObservable } from "mobx";
import { ModelessFormStore } from "src/components/ModelessForm";
import { TreeNode } from "./node";
import { ResourceState } from "./resourceState";
import { SelectionToken } from "./selection";

/**
 * Данные формы для выбранного узла.
 *
 * Единственный механизм защиты от гонок здесь — пропуск выбора: ответ, взятый
 * для прежнего узла (или для того же, но до перевыбора), в форму не попадает.
 * В v1 для этого было два независимых счётчика.
 *
 * Загрузчик приходит снаружи: иначе домен зависел бы от слоя вариантов.
 */
export class FormDataStore {
  state: ResourceState<unknown> = { status: "none" };

  constructor(
    private readonly formStore: ModelessFormStore,
    private readonly isActual: (token: SelectionToken) => boolean,
    private readonly loadData: (node: TreeNode) => Promise<unknown>,
  ) {
    makeAutoObservable<FormDataStore, "formStore" | "isActual" | "loadData">(
      this,
      {
        formStore: false,
        isActual: false,
        loadData: false,
      },
    );
  }

  private setState(next: ResourceState<unknown>) {
    this.state = next;
  }

  /** Выбор снят: форме нечего показывать. */
  clear() {
    this.setState({ status: "none" });
    this.formStore.unsafeLoad(null);
  }

  /** Ожидание началось: данные объекта ещё грузятся. */
  markWaiting(token: SelectionToken) {
    if (this.isActual(token)) this.setState({ status: "wait" });
  }

  /** Данные объекта получить не удалось: показывать форму нечем. */
  fail(error: unknown, token: SelectionToken) {
    if (this.isActual(token)) this.setState({ status: "error", error });
  }

  async load(node: TreeNode, token: SelectionToken, isNewItem?: boolean) {
    this.markWaiting(token);
    try {
      const value = await this.loadData(node);
      if (!this.isActual(token)) return;
      this.setState({ status: "ready", value });
      this.formStore.unsafeLoad(value, isNewItem);
    } catch (error) {
      this.fail(error, token);
    }
  }
}
